import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import Multiselect from "@cloudscape-design/components/multiselect";
import Table from "@cloudscape-design/components/table";
import FormField from "@cloudscape-design/components/form-field";
import Container from "@cloudscape-design/components/container";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Textarea from "@cloudscape-design/components/textarea";
import Tabs from "@cloudscape-design/components/tabs";
import Toggle from "@cloudscape-design/components/toggle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { tournamentsAddModalStore } from "../stores/tournamentsAddModal";
import { tournamentsStore } from "../stores/tournaments";
import { gameStore } from "../stores/games";
import Poster from "../assets/poster.png";
import ProgressiveImage from "react-progressive-graceful-image";

const MAX_TOURNAMENT_BOTS = 10;

// Sum of winnings amounts are entered in rubles and stored in kopecks.
const WINNINGS_AMOUNT_MULTIPLIER = 100;
const PROVIDER_IDS_KEY = "provider_ids";
const GAME_IDS_KEY = "game_ids";

class TournamentsAddModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			games: [],
			filteredGames: [],
			templates: [],
			filterProvider: [],
			prizeDistributionJson: "", // JSON-строка для редактора
			jsonError: "", // Ошибка валидации JSON
			gameProviderDraft: "",
			gameProviderError: "",
			gameProviderTabId: "regular",
			botConfigDraft: "",
			botConfigError: "",
			botConfigTabId: "regular",
		};
	}

	handleProviderSearch(filteringText) {
		const { game } = this.props;
		const filterProvider = game.providers.filter(provider =>
			provider.name.toLowerCase().includes(filteringText.toLowerCase())
		);
		this.setState({ filterProvider: filterProvider });
	}

	parseStoredSelection = (storedSelection) => {
		if (!storedSelection) {
			return [];
		}

		return typeof storedSelection === "string" ? JSON.parse(storedSelection) : storedSelection;
	};

	getSelectionValue = (item) => {
		if (item && typeof item === "object" && "value" in item) {
			return item.value;
		}

		return item;
	};

	getSelectionLabel = (item, fallbackValue) => {
		if (item && typeof item === "object" && typeof item.label === "string" && item.label.trim()) {
			return item.label;
		}

		return fallbackValue;
	};

	getSelectedIds = (selection = []) => selection
		.map((item) => this.getSelectionValue(item))
		.filter((item) => item !== undefined && item !== null && item !== "")
		.map((item) => item.toString());

	getProviderOption = (provider) => ({
		value: provider.id.toString(),
		label: `${provider.name} (${provider.id})`,
	});

	getGameOption = (gameItem) => ({
		value: gameItem.gameID.toString(),
		label: `${gameItem.name} (gameID: ${gameItem.gameID})`,
	});

	getProviderOptions = (providers = []) => providers.map((provider) => this.getProviderOption(provider));

	getGameOptionsByProviders = (selectedProviders) => {
		const { game } = this.props;
		const selectedProviderIds = new Set(this.getSelectedIds(selectedProviders));

		return game.data
			.filter((gameItem) => selectedProviderIds.has(gameItem.providerID.toString()) && gameItem.active === 1)
			.map((gameItem) => this.getGameOption(gameItem));
	};

	prepareProviderSelection = (selection = [], providers = this.props.game.providers) => {
		const providerMap = new Map(providers.map((provider) => [provider.id.toString(), provider]));

		return selection.map((item) => {
			const providerId = this.getSelectionValue(item).toString();
			const provider = providerMap.get(providerId);

			if (provider) {
				return this.getProviderOption(provider);
			}

			return {
				value: providerId,
				label: this.getSelectionLabel(item, providerId),
			};
		});
	};

	prepareGameSelection = (selection = [], games = this.props.game.data) => {
		const gameMap = new Map(games.map((gameItem) => [gameItem.gameID.toString(), gameItem]));

		return selection.map((item) => {
			const gameId = this.getSelectionValue(item).toString();
			const gameItem = gameMap.get(gameId);

			if (gameItem) {
				return this.getGameOption(gameItem);
			}

			return {
				value: gameId,
				label: this.getSelectionLabel(item, gameId),
			};
		});
	};

	buildGameProviderDraft = (
		providerSelection = this.props.tournamentsAddModal.provider_ids,
		gameSelection = this.props.tournamentsAddModal.game_ids
	) => JSON.stringify(
		{
			[PROVIDER_IDS_KEY]: this.getSelectedIds(providerSelection).map((id) => Number(id)),
			[GAME_IDS_KEY]: this.getSelectedIds(gameSelection).map((id) => Number(id)),
		},
		null,
		2
	);

	isSumOfWinningsTournament = (tournamentType = this.props.tournamentsAddModal.tournamentType) => tournamentType === "sum_of_winnings";

	toWinningsDisplayValue = (value) => {
		if (value === "" || value === null || typeof value === "undefined") {
			return value;
		}

		const numberValue = Number(value);
		return Number.isNaN(numberValue) ? value : numberValue / WINNINGS_AMOUNT_MULTIPLIER;
	};

	toWinningsStoredValue = (value) => Math.round(Number(value) * WINNINGS_AMOUNT_MULTIPLIER);

	prepareBotConfigForDisplay = (botConfig) => {
		let preparedValue = botConfig;
		if (typeof preparedValue === "string") {
			try {
				preparedValue = JSON.parse(preparedValue);
			} catch (error) {
				return botConfig;
			}
		}

		if (!preparedValue || typeof preparedValue !== "object" || !Array.isArray(preparedValue.bots)) {
			return botConfig;
		}

		return {
			...preparedValue,
			bots: preparedValue.bots.map((bot) => {
				if (!bot || typeof bot !== "object") {
					return bot;
				}

				return {
					...bot,
					...(typeof bot.targetWinnings !== "undefined" ? { targetWinnings: this.toWinningsDisplayValue(bot.targetWinnings) } : {}),
					...(bot.winAmount && typeof bot.winAmount === "object"
						? {
								winAmount: {
									...bot.winAmount,
									min: this.toWinningsDisplayValue(bot.winAmount.min),
									max: this.toWinningsDisplayValue(bot.winAmount.max),
								},
							}
						: {}),
				};
			}),
		};
	};

	getEmptyBot = (slot) => ({
		slot,
		targetBets: "",
		targetWinnings: "",
		winAmount: {
			min: "",
			max: "",
		},
		betInterval: {
			min: "",
			max: "",
		},
		pauseDuration: {
			min: "",
			max: "",
		},
		pausesPerDay: {
			min: "",
			max: "",
		},
		startDelayMinutes: {
			min: "",
			max: "",
		},
	});

	getEmptyBotConfig = () => ({
		enabled: false,
		bots: [],
	});

	prepareBotConfig = (botConfig = this.props.tournamentsAddModal.configBot) => {
		let preparedValue = botConfig;

		if (typeof preparedValue === "string") {
			try {
				preparedValue = JSON.parse(preparedValue);
			} catch (error) {
				preparedValue = this.getEmptyBotConfig();
			}
		}

		if (!preparedValue || typeof preparedValue !== "object" || Array.isArray(preparedValue)) {
			preparedValue = this.getEmptyBotConfig();
		}

		const rawBots = Array.isArray(preparedValue.bots) ? preparedValue.bots.slice(0, MAX_TOURNAMENT_BOTS) : [];

		return {
			enabled: Boolean(preparedValue.enabled),
			bots: rawBots.map((bot, index) => {
				const emptyBot = this.getEmptyBot(index + 1);
				const rawBot = bot && typeof bot === "object" ? bot : {};

				return {
					...emptyBot,
					...rawBot,
					slot: index + 1,
					winAmount: {
						...emptyBot.winAmount,
						...(rawBot.winAmount && typeof rawBot.winAmount === "object" ? rawBot.winAmount : {}),
					},
					betInterval: {
						...emptyBot.betInterval,
						...(rawBot.betInterval && typeof rawBot.betInterval === "object" ? rawBot.betInterval : {}),
					},
					pauseDuration: {
						...emptyBot.pauseDuration,
						...(rawBot.pauseDuration && typeof rawBot.pauseDuration === "object" ? rawBot.pauseDuration : {}),
					},
					pausesPerDay: {
						...emptyBot.pausesPerDay,
						...(rawBot.pausesPerDay && typeof rawBot.pausesPerDay === "object" ? rawBot.pausesPerDay : {}),
					},
					startDelayMinutes: {
						...emptyBot.startDelayMinutes,
						...(rawBot.startDelayMinutes && typeof rawBot.startDelayMinutes === "object" ? rawBot.startDelayMinutes : {}),
					},
				};
			}),
		};
	};

	getDraftBotNumberValue = (value) =>
		value === "" || value === null || typeof value === "undefined" ? "" : Number(value);

	buildBotConfigDraft = (botConfig = this.props.tournamentsAddModal.configBot) => {
		const preparedConfig = this.prepareBotConfig(botConfig);
		const isSumOfWinnings = this.isSumOfWinningsTournament();

		return JSON.stringify(
			{
				enabled: preparedConfig.enabled,
				bots: preparedConfig.bots.map((bot, index) => ({
					slot: index + 1,
					...(isSumOfWinnings
						? {
								targetWinnings: this.getDraftBotNumberValue(bot.targetWinnings),
								winAmount: {
									min: this.getDraftBotNumberValue(bot.winAmount.min),
									max: this.getDraftBotNumberValue(bot.winAmount.max),
								},
							}
						: { targetBets: this.getDraftBotNumberValue(bot.targetBets) }),
					betInterval: {
						min: this.getDraftBotNumberValue(bot.betInterval.min),
						max: this.getDraftBotNumberValue(bot.betInterval.max),
					},
					pauseDuration: {
						min: this.getDraftBotNumberValue(bot.pauseDuration.min),
						max: this.getDraftBotNumberValue(bot.pauseDuration.max),
					},
					pausesPerDay: {
						min: this.getDraftBotNumberValue(bot.pausesPerDay.min),
						max: this.getDraftBotNumberValue(bot.pausesPerDay.max),
					},
					startDelayMinutes: {
						min: this.getDraftBotNumberValue(bot.startDelayMinutes.min),
						max: this.getDraftBotNumberValue(bot.startDelayMinutes.max),
					},
				})),
			},
			null,
			2
		);
	};

	syncBotConfigDraft = (botConfig = this.props.tournamentsAddModal.configBot) => {
		this.setState({
			botConfigDraft: this.buildBotConfigDraft(botConfig),
			botConfigError: "",
		});
	};

	getIntegerFieldError = (value, label, allowZero = true) => {
		if (value === "" || value === null || typeof value === "undefined") {
			return `${label} is required`;
		}

		const numberValue = Number(value);
		if (!Number.isInteger(numberValue) || numberValue < 0 || (!allowZero && numberValue === 0)) {
			return `${label} must be ${allowZero ? "a non-negative integer" : "a positive integer"}`;
		}

		return "";
	};

	getNumberFieldError = (value, label, allowZero = true) => {
		if (value === "" || value === null || typeof value === "undefined") {
			return `${label} is required`;
		}

		const numberValue = Number(value);
		if (Number.isNaN(numberValue) || numberValue < 0 || (!allowZero && numberValue === 0)) {
			return `${label} must be ${allowZero ? "a non-negative number" : "a positive number"}`;
		}

		return "";
	};

	getRangeFieldError = (range, label, allowDecimal = false) => {
		const minError = allowDecimal
			? this.getNumberFieldError(range.min, `${label} min`)
			: this.getIntegerFieldError(range.min, `${label} min`);
		if (minError) {
			return minError;
		}

		const maxError = allowDecimal
			? this.getNumberFieldError(range.max, `${label} max`)
			: this.getIntegerFieldError(range.max, `${label} max`);
		if (maxError) {
			return maxError;
		}

		if (Number(range.min) > Number(range.max)) {
			return `${label} min cannot exceed max`;
		}

		return "";
	};

	validateBotConfig = (botConfig = this.props.tournamentsAddModal.configBot) => {
		const preparedConfig = this.prepareBotConfig(botConfig);

		if (preparedConfig.bots.length > MAX_TOURNAMENT_BOTS) {
			return `Bot config cannot contain more than ${MAX_TOURNAMENT_BOTS} bots`;
		}

		if (preparedConfig.enabled && preparedConfig.bots.length === 0) {
			return "Add at least one bot or disable bot config";
		}

		if (!preparedConfig.enabled && preparedConfig.bots.length === 0) {
			return "";
		}

		const isSumOfWinnings = this.isSumOfWinningsTournament();

		for (let index = 0; index < preparedConfig.bots.length; index += 1) {
			const bot = preparedConfig.bots[index];
			const prefix = `Bot #${index + 1}`;

			if (isSumOfWinnings) {
				const targetWinningsError = this.getNumberFieldError(bot.targetWinnings, `${prefix} targetWinnings`, false);
				if (targetWinningsError) {
					return targetWinningsError;
				}

				const winAmountError = this.getRangeFieldError(bot.winAmount, `${prefix} winAmount`, true);
				if (winAmountError) {
					return winAmountError;
				}

				if (Number(bot.winAmount.min) <= 0) {
					return `${prefix} winAmount min must be a positive number`;
				}
			} else {
				const targetBetsError = this.getIntegerFieldError(bot.targetBets, `${prefix} targetBets`);
				if (targetBetsError) {
					return targetBetsError;
				}
			}

			const betIntervalError = this.getRangeFieldError(bot.betInterval, `${prefix} betInterval`, true);
			if (betIntervalError) {
				return betIntervalError;
			}

			const pauseDurationError = this.getRangeFieldError(bot.pauseDuration, `${prefix} pauseDuration`);
			if (pauseDurationError) {
				return pauseDurationError;
			}

			const pausesPerDayError = this.getRangeFieldError(bot.pausesPerDay, `${prefix} pausesPerDay`);
			if (pausesPerDayError) {
				return pausesPerDayError;
			}

			const startDelayMinutesError = this.getRangeFieldError(bot.startDelayMinutes, `${prefix} startDelayMinutes`);
			if (startDelayMinutesError) {
				return startDelayMinutesError;
			}
		}

		return "";
	};

	prepareBotConfigForSubmit = (botConfig = this.props.tournamentsAddModal.configBot) => {
		const preparedConfig = this.prepareBotConfig(botConfig);
		const isSumOfWinnings = this.isSumOfWinningsTournament();

		return {
			enabled: preparedConfig.enabled,
			bots: preparedConfig.bots.map(({ targetBets, targetWinnings, winAmount, ...bot }, index) => ({
				...bot,
				slot: index + 1,
				uid: null,
				...(isSumOfWinnings
					? {
							targetWinnings: this.toWinningsStoredValue(targetWinnings),
							winAmount: {
								min: this.toWinningsStoredValue(winAmount.min),
								max: this.toWinningsStoredValue(winAmount.max),
							},
						}
					: { targetBets: Number(targetBets) }),
				betInterval: {
					min: Number(bot.betInterval.min),
					max: Number(bot.betInterval.max),
				},
				pauseDuration: {
					min: Number(bot.pauseDuration.min),
					max: Number(bot.pauseDuration.max),
				},
				pausesPerDay: {
					min: Number(bot.pausesPerDay.min),
					max: Number(bot.pausesPerDay.max),
				},
				startDelayMinutes: {
					min: Number(bot.startDelayMinutes.min),
					max: Number(bot.startDelayMinutes.max),
				},
			})),
		};
	};

	updateBotConfigState = (nextConfig) => {
		const { tournamentsAddModal, setTournamentsAddModal } = this.props;
		const preparedConfig = this.prepareBotConfig(nextConfig);

		setTournamentsAddModal({
			...tournamentsAddModal,
			configBot: preparedConfig,
		});
		this.syncBotConfigDraft(preparedConfig);
	};

	handleBotConfigToggle = (enabled) => {
		const preparedConfig = this.prepareBotConfig();
		this.updateBotConfigState({
			...preparedConfig,
			enabled,
		});
	};

	addBotConfigBot = () => {
		const { Interface } = this.props;
		const preparedConfig = this.prepareBotConfig();

		if (preparedConfig.bots.length >= MAX_TOURNAMENT_BOTS) {
			Interface.addNoty("Error", `A maximum of ${MAX_TOURNAMENT_BOTS} bots is allowed`);
			return;
		}

		this.updateBotConfigState({
			...preparedConfig,
			bots: [...preparedConfig.bots, this.getEmptyBot(preparedConfig.bots.length + 1)],
		});
	};

	removeBotConfigBot = (index) => {
		const preparedConfig = this.prepareBotConfig();
		this.updateBotConfigState({
			...preparedConfig,
			bots: preparedConfig.bots.filter((_, botIndex) => botIndex !== index),
		});
	};

	updateBotConfigBotField = (index, field, value, nestedField = null) => {
		const preparedConfig = cloneDeep(this.prepareBotConfig());

		if (!preparedConfig.bots[index]) {
			return;
		}

		if (nestedField) {
			preparedConfig.bots[index][nestedField][field] = value;
		} else {
			preparedConfig.bots[index][field] = value;
		}

		this.updateBotConfigState(preparedConfig);
	};

	handleBotConfigTabChange = ({ detail }) => {
		if (detail.activeTabId === "json") {
			this.syncBotConfigDraft();
		}

		this.setState({
			botConfigTabId: detail.activeTabId,
			botConfigError: "",
		});
	};

	handleBotConfigDraftChange = (value) => {
		const { tournamentsAddModal, setTournamentsAddModal } = this.props;
		this.setState({
			botConfigDraft: value,
		});

		try {
			const preparedConfig = this.parseBotConfigInput(value);
			setTournamentsAddModal({
				...tournamentsAddModal,
				configBot: preparedConfig,
			});
			this.setState({
				botConfigError: "",
			});
		} catch (error) {
			this.setState({
				botConfigError: error.message,
			});
		}
	};

	copyBotConfigSelection = async () => {
		const { tournamentsAddModal, Interface } = this.props;
		const value = this.buildBotConfigDraft(tournamentsAddModal.configBot);

		try {
			await this.copyTextToClipboard(value);
			Interface.addNoty("Success", "Copied bot config JSON");
		} catch (error) {
			Interface.addNoty("Error", "Failed to copy bot config");
		}
	};

	parseBotConfigInput = (value) => {
		const trimmedValue = value.trim();

		if (!trimmedValue) {
			throw new Error("Input is empty");
		}

		let parsedValue;
		try {
			parsedValue = JSON.parse(trimmedValue);
		} catch (error) {
			throw new Error(`Invalid JSON format: ${error.message}`);
		}

		if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
			throw new Error("JSON must be an object with enabled and bots fields");
		}

		const preparedConfig = this.prepareBotConfig(parsedValue);
		const validationError = this.validateBotConfig(preparedConfig);

		if (validationError) {
			throw new Error(validationError);
		}

		return preparedConfig;
	};

	getValidImportedIds = (rawIds, fieldName) => {
		if (!Array.isArray(rawIds)) {
			throw new Error(`${fieldName} must be an array of IDs`);
		}

		const importedIds = rawIds.map((rawId) => {
			const preparedId =
				typeof rawId === "number"
					? rawId
					: typeof rawId === "string" && rawId.trim() !== ""
						? Number(rawId.trim())
						: NaN;

			if (!Number.isInteger(preparedId) || preparedId <= 0) {
				throw new Error(`${fieldName} must contain positive integer IDs only`);
			}

			return preparedId.toString();
		});
		const duplicateIds = [...new Set(importedIds.filter((id, index) => importedIds.indexOf(id) !== index))];

		if (duplicateIds.length > 0) {
			throw new Error(`${fieldName} contains duplicate IDs: ${duplicateIds.join(", ")}`);
		}

		return importedIds;
	};

	parseGameProviderInput = (value) => {
		const trimmedValue = value.trim();

		if (!trimmedValue) {
			throw new Error("Input is empty");
		}

		let providerIdsValue;
		let gameIdsValue;

		if (trimmedValue.startsWith("{") || trimmedValue.startsWith("[")) {
			let parsedValue;

			try {
				parsedValue = JSON.parse(trimmedValue);
			} catch (error) {
				throw new Error(`Invalid JSON format: ${error.message}`);
			}

			if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
				throw new Error("JSON must be an object with provider_ids and game_ids arrays");
			}

			providerIdsValue = parsedValue[PROVIDER_IDS_KEY];
			gameIdsValue = parsedValue[GAME_IDS_KEY];
		} else {
			const rows = trimmedValue
				.split(/\r?\n/)
				.map((line) => line.trim())
				.filter(Boolean);
			const parsedRows = {};

			if (rows.length === 0) {
				throw new Error("CSV input is empty");
			}

			rows.forEach((row) => {
				const columns = row.split(",").map((column) => column.trim());
				const rowKey = columns[0];

				if (rowKey !== PROVIDER_IDS_KEY && rowKey !== GAME_IDS_KEY) {
					throw new Error("CSV rows must start with provider_ids or game_ids");
				}

				if (rowKey in parsedRows) {
					throw new Error(`CSV contains duplicate '${rowKey}' rows`);
				}

				parsedRows[rowKey] = columns.slice(1).filter((column) => column !== "");
			});

			if (!(PROVIDER_IDS_KEY in parsedRows) || !(GAME_IDS_KEY in parsedRows)) {
				throw new Error("CSV must contain both 'provider_ids' and 'game_ids' rows");
			}

			providerIdsValue = parsedRows[PROVIDER_IDS_KEY];
			gameIdsValue = parsedRows[GAME_IDS_KEY];
		}

		const providerIds = this.getValidImportedIds(providerIdsValue, PROVIDER_IDS_KEY);
		const gameIds = this.getValidImportedIds(gameIdsValue, GAME_IDS_KEY);
		const { game } = this.props;
		const providerMap = new Map(game.providers.map((provider) => [provider.id.toString(), provider]));
		const gameMap = new Map(game.data.map((gameItem) => [gameItem.gameID.toString(), gameItem]));
		const unknownProviderIds = providerIds.filter((providerId) => !providerMap.has(providerId));
		const unknownGameIds = gameIds.filter((gameId) => !gameMap.has(gameId));

		if (unknownProviderIds.length > 0) {
			throw new Error(`Unknown provider IDs: ${unknownProviderIds.join(", ")}`);
		}

		if (unknownGameIds.length > 0) {
			throw new Error(`Unknown game IDs: ${unknownGameIds.join(", ")}`);
		}

		const providerIdSet = new Set(providerIds);
		const wrongGameIds = gameIds.filter((gameId) => {
			const gameItem = gameMap.get(gameId);
			return !providerIdSet.has(gameItem.providerID.toString());
		});

		if (wrongGameIds.length > 0) {
			throw new Error(`These games do not belong to the selected providers: ${wrongGameIds.join(", ")}`);
		}

		return {
			providerIds,
			gameIds,
			providerOptions: providerIds.map((providerId) => this.getProviderOption(providerMap.get(providerId))),
			gameOptions: gameIds.map((gameId) => this.getGameOption(gameMap.get(gameId))),
		};
	};

	syncGameProviderDraft = (providerSelection = this.props.tournamentsAddModal.provider_ids, gameSelection = this.props.tournamentsAddModal.game_ids) => {
		this.setState({
			gameProviderDraft: this.buildGameProviderDraft(providerSelection, gameSelection),
			gameProviderError: "",
		});
	};

	copyTextToClipboard = async (value) => {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			await navigator.clipboard.writeText(value);
			return;
		}

		const textarea = document.createElement("textarea");
		textarea.value = value;
		textarea.setAttribute("readonly", "");
		textarea.style.position = "absolute";
		textarea.style.left = "-9999px";
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand("copy");
		document.body.removeChild(textarea);
	};

	copyGameProviderSelection = async () => {
		const { tournamentsAddModal, Interface } = this.props;
		const value = this.buildGameProviderDraft(
			tournamentsAddModal.provider_ids,
			tournamentsAddModal.game_ids
		);

		try {
			await this.copyTextToClipboard(value);
			Interface.addNoty("Success", "Copied JSON set");
		} catch (error) {
			Interface.addNoty("Error", "Failed to copy selection");
		}
	};

	handleGameProviderTabChange = ({ detail }) => {
		if (detail.activeTabId === "json") {
			this.syncGameProviderDraft();
		}

		this.setState({
			gameProviderTabId: detail.activeTabId,
			gameProviderError: "",
		});
	};

	handleGameProviderDraftChange = (value) => {
		this.setState({
			gameProviderDraft: value,
			gameProviderError: "",
		});
	};

	applyGameProviderSelection = () => {
		const { game, tournamentsAddModal, setTournamentsAddModal } = this.props;

		try {
			const parsedSet = this.parseGameProviderInput(this.state.gameProviderDraft);
			const nextGames = this.getGameOptionsByProviders(parsedSet.providerIds);

			setTournamentsAddModal({
				...tournamentsAddModal,
				provider_ids: parsedSet.providerOptions,
				game_ids: parsedSet.gameOptions,
			});

			this.setState({
				games: nextGames,
				filteredGames: nextGames.slice(0, 30),
				gameProviderDraft: this.buildGameProviderDraft(
					parsedSet.providerOptions,
					parsedSet.gameOptions
				),
				gameProviderError: "",
			});
		} catch (error) {
			this.setState({
				gameProviderError: error.message,
			});
		}
	};

	componentDidMount() {
		this.resetFormFields();
		this.loadTemplates();
	}

	loadTemplates = () => {
		const { Interface } = this.props;
		Interface.request("get", {
			class: "data",
			module: "tournaments",
			method: "list",
		}, {
			template: "all",
			page: 1,
			type: "tournaments",
			limit: 500,
		}).then((result) => {
			this.setState({
				templates: result.data,
			});
		}).catch((error) => {
			console.error("Failed to load templates:", error);
		});
	};

	handleTemplateChange = (templateId) => {
		const { setTournamentsAddModal, tournamentsAddModal, game } = this.props;
		if (!templateId) {
			this.resetFormFields();
			return;
		}

		const selectedTemplate = this.state.templates.find(template => template.id.toString() === templateId);
		if (!selectedTemplate) {
			this.props.Interface.addNoty("Error", "Selected template not found");
			return;
		}


		const prizeDistribution = selectedTemplate.prize_distribution
			? JSON.parse(selectedTemplate.prize_distribution)
				.filter((element) => element.prize)
				.map((element) => ({ ...element, prize: (element.prize) / 100 }))
				.slice(0, selectedTemplate.winners)
			: [];
		const selectedProviderIds = this.parseStoredSelection(selectedTemplate.provider_ids);
		const selectedGameIds = this.parseStoredSelection(selectedTemplate.game_ids);
		const providerOptions = this.prepareProviderSelection(selectedProviderIds, game.providers);
		const gameOptions = this.prepareGameSelection(selectedGameIds, game.data);
		const preparedBotConfig = this.prepareBotConfig(
			this.prepareBotConfigForDisplay(selectedTemplate.bot_config || selectedTemplate.config_bot)
		);

		setTournamentsAddModal({
			...tournamentsAddModal,
			template_id: templateId,
			name: selectedTemplate.name || "",
			slug: selectedTemplate.slug || "",
			tournamentType: selectedTemplate.type || "",
			periodType: selectedTemplate.period || "",
			game_ids: gameOptions,
			provider_ids: providerOptions,
			from: selectedTemplate.start_time,
			to: selectedTemplate.end_time,
			winners: selectedTemplate.winners ? selectedTemplate.winners.toString() : "",
			prize_pool: selectedTemplate.prize_pool ? (selectedTemplate.prize_pool / 100).toString() : "",
			min_bet: selectedTemplate.minbet ? (selectedTemplate.minbet / 100).toString() : "",
			status: selectedTemplate.status || "active",
			banner: selectedTemplate.banner_url || null,
			vip_level: selectedTemplate.vip_lvl !== null ? selectedTemplate.vip_lvl.toString() : null,
			deposit_count: selectedTemplate.deposit_count ? selectedTemplate.deposit_count.toString() : "0",
			promo_scope: selectedTemplate.promo_scope || "tournament_games",
			configBot: preparedBotConfig,
			prizeDistribution,
		});

		// Обновляем JSON-представление
		this.setState({
			prizeDistributionJson: JSON.stringify(prizeDistribution, null, 2),
			jsonError: "",
			gameProviderDraft: this.buildGameProviderDraft(providerOptions, gameOptions),
			gameProviderError: "",
			gameProviderTabId: "regular",
			botConfigDraft: this.buildBotConfigDraft(preparedBotConfig),
			botConfigError: "",
			botConfigTabId: "regular",
		});

		this.filterGamesByProviders(selectedProviderIds);
	};

	addTournament = async () => {
		const { tournamentsAddModal, Interface, setTournamentsAddModal } = this.props;

		if (tournamentsAddModal.name === "") {
			Interface.addNoty("Error", "Tournament name is required");
			return;
		}

		if (tournamentsAddModal.translationType === "") {
			Interface.addNoty("Error", "TranslationType is required");
			return;
		}

		if (tournamentsAddModal.tournamentType === "") {
			Interface.addNoty("Error", "Tournament type is required");
			return;
		}
		if (tournamentsAddModal.slug && !/^[a-z0-9_]{1,30}$/.test(tournamentsAddModal.slug)) {
			Interface.addNoty("Error", "Slug must be 1-30 latin characters, numbers or underscore");
			return;
		}

		const totalDistributed = tournamentsAddModal.prizeDistribution.reduce(
			(sum, item) => sum + Number(item.prize || 0),
			0
		);
		const prizePool = Number(tournamentsAddModal.prize_pool) || 0;
		if (totalDistributed !== prizePool) {
			Interface.addNoty("Error", "Prize pool doesn't match the distributed total. Please check the distribution.");
			return;
		}

		if (this.state.botConfigError) {
			Interface.addNoty("Error", this.state.botConfigError);
			return;
		}

		const botConfigError = this.validateBotConfig(tournamentsAddModal.configBot);
		if (botConfigError) {
			Interface.addNoty("Error", botConfigError);
			return;
		}

		this.setState({ loading: true });
		const body = {
			...tournamentsAddModal,
			games: tournamentsAddModal.game_ids.map(element => parseInt(element.value)),
			providers: tournamentsAddModal.provider_ids.map(element => parseInt(element.value)),
			type: tournamentsAddModal.modalType,
			min_bet: tournamentsAddModal.min_bet === "" ? null : Number(tournamentsAddModal.min_bet),
			prize_pool: Number(tournamentsAddModal.prize_pool),
			deposit_count: Number(tournamentsAddModal.deposit_count),
			configBot: this.prepareBotConfigForSubmit(tournamentsAddModal.configBot),
			bot_config: this.prepareBotConfigForSubmit(tournamentsAddModal.configBot),
		};

		try {
			const result = await Interface.request("set", {
				class: "data",
				module: "tournaments",
				method: "add"
			}, body);

			Interface.addNoty("Success", "Tournament was successfully added");
			setTournamentsAddModal({ ...tournamentsAddModal, show: false });
			Interface.loadTournamentList(undefined, tournamentsAddModal.modalType);
		} catch (error) {
			Interface.addNoty("Error", error.text || error.toString());
		} finally {
			this.setState({ loading: false });
		}
	};

	filterGamesByProviders(selectedProviders) {
		const options = this.getGameOptionsByProviders(selectedProviders);

		this.setState({
			games: options,
			filteredGames: options.slice(0, 30),
		});
	}

	handleProviderChange(selectedProviders) {
		this.props.setTournamentsAddModal({
			...this.props.tournamentsAddModal,
			provider_ids: selectedProviders,
			game_ids: selectedProviders.length === 0 ? [] : this.props.tournamentsAddModal.game_ids
		});
		this.filterGamesByProviders(selectedProviders.map(provider => provider.value));
	}

	handleGameSearch(filteringText) {
		const filteredGames = this.state.games.filter(game =>
			game.label.toLowerCase().includes(filteringText.toLowerCase())
		);
		this.setState({ filteredGames: filteredGames.slice(0, 30) });
	}

	resetFormFields = () => {
		this.setState({
			loading: false,
			games: [],
			filteredGames: [],
			templates: [],
			filterProvider: [],
			prizeDistributionJson: JSON.stringify([{ place: 1, prize: "" }], null, 2),
			jsonError: "",
			gameProviderDraft: this.buildGameProviderDraft([], []),
			gameProviderError: "",
			gameProviderTabId: "regular",
			botConfigDraft: this.buildBotConfigDraft(this.getEmptyBotConfig()),
			botConfigError: "",
			botConfigTabId: "regular",
		});
		this.props.setTournamentsAddModal({
			...this.props.tournamentsAddModal,
			name: "",
			template_id: null,
			currentOption: {
				tournamentType: [
					{ value: "sum_of_bets", label: "sum of bets" },
					{ value: "highest_coefficient", label: "highest coefficient" },
					{ value: "number_of_bets", label: "number of bets" },
					{ value: "sum_of_winnings", label: "sum of winnings" }
				],
				periodType: [
					{ value: "daily", label: "daily" },
					{ value: "monthly", label: "monthly" },
					{ value: "weekly", label: "weekly" }
				],
				translationType: [
					{ value: "internation", label: "internation" },
					{ value: "local", label: "local" },
				],
			},
			recurrence: "",
			tournamentType: "",
			translationType: "",
			periodType: "",
			game_ids: [],
			provider_ids: [],
			winners: "",
			prize_pool: "",
			from: "",
			to: "",
			min_bet: "",
			status: "active",
			banner: null,
			slug: "",
			vip_level: null,
			deposit_count: "0",
			promo_scope: "tournament_games",
			projects: [],
			configBot: this.getEmptyBotConfig(),
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
			prizeDistribution: [{ place: 1, prize: "" }],
			distributionMethod: "manual",
		});
	};

	updateParam = (key, value) => {
		const { tournamentsAddModal, setTournamentsAddModal } = this.props;
		const updatedState = cloneDeep(tournamentsAddModal);
		updatedState[key] = value;
		setTournamentsAddModal(updatedState);

		if (key === "winners") {
			setTimeout(() => {
				this.updatePrizeDistributionTable(Number(value));
			}, 0);
		}

		if (key === "prize_pool" && (tournamentsAddModal.distributionMethod === "even" || tournamentsAddModal.distributionMethod === "competition")) {
			setTimeout(() => {
				this.updatePrizeDistributionTable(Number(tournamentsAddModal.winners));
			}, 0);
		}
	};

	updatePrizeDistribution = (index, field, value) => {
		const { tournamentsAddModal, setTournamentsAddModal } = this.props;
		const newDistribution = [...tournamentsAddModal.prizeDistribution];
		newDistribution[index] = { ...newDistribution[index], [field]: value };
		setTournamentsAddModal({ ...tournamentsAddModal, prizeDistribution: newDistribution });

		this.setState({
			prizeDistributionJson: JSON.stringify(newDistribution, null, 2),
			jsonError: "",
		});
	};

	updatePrizeDistributionTable = (winnersCount) => {
		const { tournamentsAddModal, setTournamentsAddModal } = this.props;
		const { prize_pool, distributionMethod } = tournamentsAddModal;

		if (!winnersCount || winnersCount < 1) {
			const defaultDistribution = [{ place: 1, prize: "" }];
			setTournamentsAddModal({
				...tournamentsAddModal,
				prizeDistribution: defaultDistribution,
			});
			this.setState({
				prizeDistributionJson: JSON.stringify(defaultDistribution, null, 2),
				jsonError: "",
			});
			return;
		}

		const newDistribution = Array.from({ length: winnersCount }, (_, index) => ({
			place: index + 1,
			prize: distributionMethod === "even" && prize_pool
				? Math.floor(Number(prize_pool) / winnersCount)
				: "",
		}));

		setTournamentsAddModal({
			...tournamentsAddModal,
			winners: winnersCount.toString(),
			prizeDistribution: newDistribution,
		});

		// Обновляем JSON
		this.setState({
			prizeDistributionJson: JSON.stringify(newDistribution, null, 2),
			jsonError: "",
		});
	};

	handleDistributionMethodChange = (method) => {
		const { tournamentsAddModal, setTournamentsAddModal } = this.props;
		setTournamentsAddModal({ ...tournamentsAddModal, distributionMethod: method });
		setTimeout(() => {
			this.updatePrizeDistributionTable(Number(tournamentsAddModal.winners));
		}, 0);
	};

	addPrizeRow = () => {
		const { tournamentsAddModal, setTournamentsAddModal } = this.props;
		const newDistribution = [...tournamentsAddModal.prizeDistribution];
		const lastPlace = newDistribution[newDistribution.length - 1].place;
		newDistribution.push({ place: lastPlace + 1, prize: "" });
		setTournamentsAddModal({
			...tournamentsAddModal,
			prizeDistribution: newDistribution,
			winners: newDistribution.length.toString(),
		});

		// Обновляем JSON
		this.setState({
			prizeDistributionJson: JSON.stringify(newDistribution, null, 2),
			jsonError: "",
		});
	};

	removePrizeRow = (index) => {
		const { tournamentsAddModal, setTournamentsAddModal } = this.props;
		const newDistribution = tournamentsAddModal.prizeDistribution.filter((_, i) => i !== index);
		setTournamentsAddModal({
			...tournamentsAddModal,
			prizeDistribution: newDistribution.map((item, i) => ({ ...item, place: i + 1 })),
			winners: newDistribution.length.toString(),
		});

		// Обновляем JSON
		this.setState({
			prizeDistributionJson: JSON.stringify(newDistribution, null, 2),
			jsonError: "",
		});
	};

	handleJsonChange = (jsonString) => {
		const { tournamentsAddModal, setTournamentsAddModal } = this.props;
		this.setState({ prizeDistributionJson: jsonString });

		try {
			const parsedJson = JSON.parse(jsonString);
			if (!Array.isArray(parsedJson)) {
				this.setState({ jsonError: "JSON must be an array of objects" });
				return;
			}

			// Валидация структуры
			const isValid = parsedJson.every((item, index) => {
				return (
					item &&
					typeof item === "object" &&
					"place" in item &&
					"prize" in item &&
					Number.isInteger(item.place) &&
					item.place === index + 1 &&
					(typeof item.prize === "number" || Number.isInteger(item.place))
				);
			});

			if (!isValid) {
				this.setState({ jsonError: "Invalid JSON structure. Each item must have 'place' (integer, sequential) and 'prize' (integer, sequential)." });
				return;
			}

			// Обновляем prizeDistribution
			setTournamentsAddModal({
				...tournamentsAddModal,
				prizeDistribution: parsedJson,
				winners: parsedJson.length.toString(),
			});
			this.setState({ jsonError: "" });
		} catch (error) {
			this.setState({ jsonError: "Invalid JSON format: " + error.message });
		}
	};

	render() {
		const { tournamentsAddModal, setTournamentsAddModal, game } = this.props;

		const totalDistributed = tournamentsAddModal.prizeDistribution.reduce(
			(sum, item) => sum + Number(item.prize || 0),
			0
		);
		const prizePool = Number(tournamentsAddModal.prize_pool) || 0;
		const botConfig = this.prepareBotConfig(tournamentsAddModal.configBot);

		return (
			<Modal
				visible={tournamentsAddModal.show}
				size="max"
				onDismiss={() => setTournamentsAddModal({ ...tournamentsAddModal, show: false })}
				header={`Add Tournament ${tournamentsAddModal.modalType === "tournaments" ? "" : "recurrence"}`}
				children={
					<SpaceBetween direction="vertical" size="l">
						{/* Основные настройки */}
						{tournamentsAddModal.modalType === "tournaments" && (
							<FormField
								label="Template"
								description="Select a template to pre-fill the form"
								stretch
							>
								<Select
									selectedOption={
										tournamentsAddModal.template_id
											? this.state.templates
												.filter(t => t.id.toString() === tournamentsAddModal.template_id)
												.map((template) => ({
													value: template.value,
													label: template.name,
												}))[0]
											: null
									}
									onChange={({ detail }) =>
										this.handleTemplateChange(
											detail.selectedOption ? detail.selectedOption.value.toString() : null
										)
									}
									options={[{ id: null, name: "No template" }, ...this.state.templates].map(
										template => ({
											value: template.id ? template.id.toString() : null,
											label: template.name,
										})
									)}
									disabled={this.state.templates.length === 0}
									placeholder="Select a template"
								/>
							</FormField>
						)}
						<Container header={<Box variant="h2">Basic Settings</Box>}>
							<SpaceBetween direction="vertical" size="m">
								<ColumnLayout columns={2}>
									<FormField
										label="Name"
										description="Enter the name of the tournament"
										stretch
									>
										<Input
											value={tournamentsAddModal.name}
											onChange={({ detail }) => this.updateParam("name", detail.value)}
											placeholder="Tournament Name"
										/>
									</FormField>
									<FormField
										label="Slug"
										description="Unique URL slug (latin letters, numbers, underscore, max 30 characters)"
										stretch
									>
										<Input
											value={tournamentsAddModal.slug}
											onChange={({ detail }) => this.updateParam("slug", detail.value)}
											placeholder="tournament-slug"
										/>
									</FormField>
								</ColumnLayout>

								<FormField
									label="Translation type"
									description="Translation type must be international or local"
									stretch
								>
									<Select
										selectedOption={tournamentsAddModal.translationType ? {
											value: tournamentsAddModal.translationType,
											label: tournamentsAddModal.translationType,
										} : null}
										onChange={({ detail }) => this.updateParam("translationType", detail.selectedOption.value)}
										options={tournamentsAddModal.currentOption.translationType}
										placeholder="Select translationType"
									/>
								</FormField>

								<FormField
									label="Minimum Bet (RUB)"
									description="Optional: Minimum bet to participate (1-2000 RUB)"
									stretch
								>
									<Input
										type="number"
										value={tournamentsAddModal.min_bet}
										onChange={({ detail }) => {
											const value = detail.value;
											this.updateParam(
												"min_bet",
												value === "" ? "" : Math.max(1, Math.min(2000, Number(value)))
											);
										}}
										placeholder="10 (optional)"
									/>
								</FormField>

								<SpaceBetween direction="vertical" size="m">
									<FormField
										label="Tournament Type"
										description="Select the type of tournament"
										stretch
									>
										<Select
											selectedOption={
												tournamentsAddModal.currentOption.tournamentType.find(
													t => t.value === tournamentsAddModal.tournamentType
												) || null
											}
											onChange={({ detail }) =>
												this.updateParam("tournamentType", detail.selectedOption.value)
											}
											options={tournamentsAddModal.currentOption.tournamentType}
											placeholder="Select tournament type"
										/>
									</FormField>

									{tournamentsAddModal.modalType !== "tournaments" && (
										<FormField
											label="Period Type"
											description="Select the period type for the tournament"
											stretch
										>
											<Select
												selectedOption={
													tournamentsAddModal.currentOption.periodType.find(
														p => p.value === tournamentsAddModal.periodType
													) || null
												}
												onChange={({ detail }) =>
													this.updateParam("periodType", detail.selectedOption.value)
												}
												options={tournamentsAddModal.currentOption.periodType}
												placeholder="Select period type"
											/>
										</FormField>
									)}
								</SpaceBetween>

								<FormField
									label="Status"
									description="Set the status of the tournament"
									stretch
								>
									<Select
										selectedOption={{
											value: tournamentsAddModal.status,
											label: tournamentsAddModal.status,
										}}
										onChange={({ detail }) => this.updateParam("status", detail.selectedOption.value)}
										options={[
											{ value: "active", label: "Active" },
											{ value: "finished", label: "finished" },
										]}
									/>
								</FormField>

								{tournamentsAddModal.modalType === "tournaments" && (
									<FormField
										label="Banner"
										description="Upload a banner image for the tournament"
										stretch
									>
										<SpaceBetween direction="vertical" size="m">
											<input
												type="file"
												accept="image/png"
												onChange={(event) => {
													const file = event.target.files[0];
													if (file) {
														const reader = new FileReader();
														reader.onload = (event) => {
															const result = event.target.result;
															this.updateParam("banner", result);
														};
														reader.readAsDataURL(file);
													} else {
														this.props.Interface.addNoty(
															"Error",
															"Please, select a valid image file"
														);
													}
												}}
											/>
											{tournamentsAddModal.banner && (
												<ProgressiveImage
													src={tournamentsAddModal.banner}
													placeholder={Poster}
												>
													{(src) => (
														<img
															style={{ width: "300px" }}
															src={src}
															alt={tournamentsAddModal.name}
														/>
													)}
												</ProgressiveImage>
											)}
										</SpaceBetween>
									</FormField>
								)}
							</SpaceBetween>
						</Container>

						{/* Игры и провайдеры */}
						{tournamentsAddModal.modalType === "tournaments" && (
							<Container header={<Box variant="h2">Games and Providers</Box>}>
								<Tabs
									activeTabId={this.state.gameProviderTabId}
									onChange={this.handleGameProviderTabChange}
									tabs={[
										{
											label: "Regular",
											id: "regular",
											content: (
												<SpaceBetween direction="vertical" size="m">
													<FormField
														label="Providers"
														description="Select providers for the tournament"
														stretch
													>
														<Multiselect
															filteringType="manual"
															selectedOptions={tournamentsAddModal.provider_ids}
															onLoadItems={({ detail }) =>
																this.handleProviderSearch(detail.filteringText)
															}
															onChange={({ detail }) =>
																this.handleProviderChange(detail.selectedOptions)
															}
															options={this.getProviderOptions(this.state.filterProvider)}
															disabled={game.providers.length === 0}
															placeholder="Select providers"
														/>
													</FormField>

													<FormField
														label="Games"
														description="Select games for the tournament"
														stretch
													>
														<Multiselect
															filteringType="manual"
															onLoadItems={({ detail }) =>
																this.handleGameSearch(detail.filteringText)
															}
															selectedOptions={tournamentsAddModal.game_ids}
															onChange={({ detail }) =>
																this.updateParam("game_ids", detail.selectedOptions)
															}
															options={this.state.filteredGames}
															disabled={this.state.games.length === 0}
															placeholder="Select games"
														/>
													</FormField>
												</SpaceBetween>
											),
										},
										{
											label: "JSON",
											id: "json",
											content: (
												<SpaceBetween direction="vertical" size="m">
													<Box color="text-body-secondary">
														Paste a full provider/game set in JSON or CSV. Invalid input will not change the current selection.
													</Box>
													<SpaceBetween direction="horizontal" size="xs">
														<Button onClick={this.copyGameProviderSelection}>
															Copy JSON
														</Button>
														<Button variant="primary" onClick={this.applyGameProviderSelection}>
															Apply
														</Button>
													</SpaceBetween>
													<FormField
														label="Bulk input"
														description={'Supported formats: {"provider_ids":[1,2],"game_ids":[101,102]} or provider_ids,1,2 / game_ids,101,102'}
														errorText={this.state.gameProviderError}
														stretch
													>
														<Textarea
															value={this.state.gameProviderDraft}
															onChange={({ detail }) =>
																this.handleGameProviderDraftChange(detail.value)
															}
															placeholder={`{\n  "provider_ids": [1, 2],\n  "game_ids": [101, 102]\n}\n\nprovider_ids,1,2\ngame_ids,101,102`}
															rows={12}
														/>
													</FormField>
												</SpaceBetween>
											),
										},
									]}
								/>
							</Container>
						)}

						{/* Призовой фонд */}
						<Container header={<Box variant="h2">Prize Pool</Box>}>
							<SpaceBetween direction="vertical" size="m">
								<ColumnLayout columns={2}>
									<FormField
										label="Prize Pool (RUB)"
										description="Total prize pool for the tournament"
										stretch
									>
										<Input
											type="number"
											value={tournamentsAddModal.prize_pool}
											onChange={({ detail }) =>
												this.updateParam("prize_pool", detail.value)
											}
											placeholder="10000"
										/>
									</FormField>

									<FormField
										label="Number of Winners"
										description="Number of prize places"
										stretch
									>
										<Input
											type="number"
											value={tournamentsAddModal.winners}
											onChange={({ detail }) =>
												this.updateParam(
													"winners",
													detail.value === ""
														? ""
														: Math.max(0, Math.min(500, Number(detail.value)))
												)
											}
											placeholder="1"
											inputMode="numeric"
										/>
									</FormField>
								</ColumnLayout>

								{tournamentsAddModal.modalType === "tournaments" && (
									<FormField
										label="Distribution Method"
										description="Choose how to distribute the prize pool"
										stretch
									>
										<Select
											selectedOption={
												tournamentsAddModal.distributionMethod === "even"
													? { value: "even", label: "Even Distribution" }
													: { value: "manual", label: "Manual Distribution" }
											}
											onChange={({ detail }) => {
												this.handleDistributionMethodChange(detail.selectedOption.value);
											}}
											options={[
												{ value: "even", label: "Even Distribution" },
												{ value: "manual", label: "Manual Distribution" },
											]}
										/>
									</FormField>
								)}

								{tournamentsAddModal.modalType === "tournaments" && (
									<FormField
										label="Prize Distribution"
										description={
											<Box
												color={
													totalDistributed !== prizePool
														? "text-status-error"
														: "text-status-success"
												}
												fontSize={"body-m"}
											>
												{`Total Distributed: ${totalDistributed} RUB / Prize Pool: ${prizePool} RUB`}
											</Box>
										}
										errorText={this.state.jsonError}
										stretch
									>
										<Tabs
											tabs={[
												{
													label: "Table",
													id: "table",
													content: (
														<Table
															columnDefinitions={[
																{
																	id: "place",
																	header: "Place",
																	cell: item => item.place,
																	width: 100,
																},
																{
																	id: "prize",
																	header: "Prize (RUB)",
																	cell: item => (
																		<Input
																			type="number"
																			value={item.prize}
																			onChange={({ detail }) =>
																				this.updatePrizeDistribution(
																					item.place - 1,
																					"prize",
																					detail.value
																				)
																			}
																		/>
																	),
																},
																{
																	id: "actions",
																	cell: item => (
																		<Button
																			variant="inline-link"
																			onClick={() =>
																				this.removePrizeRow(item.place - 1)
																			}
																			disabled={
																				tournamentsAddModal.prizeDistribution
																					.length === 1
																			}
																		>
																			Remove
																		</Button>
																	),
																	width: 200,
																},
															]}
															items={tournamentsAddModal.prizeDistribution}
															footer={
																<Button
																	variant="link"
																	onClick={this.addPrizeRow}
																>
																	Add Place
																</Button>
															}
														/>
													),
												},
												{
													label: "JSON",
													id: "json",
													content: (
														<Textarea
															value={this.state.prizeDistributionJson}
															onChange={({ detail }) =>
																this.handleJsonChange(detail.value)
															}
															placeholder='[
																	{ "place": 1, "prize": 1000 },
																	{ "place": 2, "prize": 500 },
																	{ "place": 3, "prize": 250 }
															]'
															rows={10}
														/>
													),
												},
											]}
										/>
									</FormField>
								)}
							</SpaceBetween>
						</Container>

						{/* Требования к участникам */}
						{tournamentsAddModal.modalType === "tournaments" && (
							<Container header={<Box variant="h2">Participant Requirements</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										label="VIP Level Requirement"
										description="Minimum VIP level to participate (optional)"
										stretch
									>
										<Select
											selectedOption={
												tournamentsAddModal.vip_level !== null
													? {
														value: tournamentsAddModal.vip_level,
														label: `Level ${tournamentsAddModal.vip_level}`,
													}
													: null
											}
											onChange={({ detail }) =>
												this.updateParam(
													"vip_level",
													detail.selectedOption ? detail.selectedOption.value : null
												)
											}
											options={[...Array(11)].map((_, i) => ({
												value: i,
												label: `Level ${i}`,
											}))}
											placeholder="No VIP requirement"
										/>
									</FormField>

									<FormField
										label="Deposits Count Required"
										description="Number of deposits to participate (0 for all players)"
										stretch
									>
										<Input
											type="number"
											value={tournamentsAddModal.deposit_count}
											onChange={({ detail }) =>
												this.updateParam(
													"deposit_count",
													detail.value === ""
														? ""
														: Math.max(0, Math.min(500, Number(detail.value)))
												)
											}
											placeholder="1"
											inputMode="numeric"
										/>
									</FormField>

									<FormField
										label="Promo Scope"
										description="Where to display the tournament promo"
										stretch
									>
										<Select
											selectedOption={{
												value: tournamentsAddModal.promo_scope,
												label:
													tournamentsAddModal.promo_scope === "all_games"
														? "All Games"
														: "Tournament Games",
											}}
											onChange={({ detail }) =>
												this.updateParam("promo_scope", detail.selectedOption.value)
											}
											options={[
												{ value: "all_games", label: "All Games" },
												{ value: "tournament_games", label: "Tournament Games" },
											]}
										/>
									</FormField>
								</SpaceBetween>
							</Container>
						)}


						{tournamentsAddModal.modalType === "tournaments" && (tournamentsAddModal.tournamentType === "number_of_bets" || this.isSumOfWinningsTournament()) && (
							<Container header={<Box variant="h2">Bot Configuration</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										label="Bot Config Enabled"
										description={`Enable bots for the tournament and configure up to ${MAX_TOURNAMENT_BOTS} bot slots`}
										stretch
									>
										<Toggle
											checked={botConfig.enabled}
											onChange={({ detail }) => this.handleBotConfigToggle(detail.checked)}
										>
											{botConfig.enabled ? "Enabled" : "Disabled"}
										</Toggle>
									</FormField>

									<Tabs
										activeTabId={this.state.botConfigTabId}
										onChange={this.handleBotConfigTabChange}
										tabs={[
											{
												label: "Regular",
												id: "regular",
												content: (
													<SpaceBetween direction="vertical" size="m">
														<Box color="text-body-secondary">
															Add, edit, or remove tournament bots. The list is limited to 10 bots and slots are reindexed automatically.
															{this.isSumOfWinningsTournament()
																? " Every Bet Interval a bot gets a random win from the Win Amount range until it reaches the target winnings. Nothing is credited during pauses."
																: ""}
														</Box>
														<Table
															columnDefinitions={[
																{
																	id: "slot",
																	header: "Slot",
																	cell: item => item.slot,
																	width: 80,
																},
																this.isSumOfWinningsTournament()
																	? {
																			id: "targetWinnings",
																			header: "Target Winnings (RUB)",
																			cell: item => (
																				<Input
																					type="number"
																					value={item.targetWinnings}
																					step="any"
																					onChange={({ detail }) =>
																						this.updateBotConfigBotField(item.slot - 1, "targetWinnings", detail.value)
																					}
																					placeholder="50000"
																				/>
																			),
																			width: 180,
																		}
																	: {
																			id: "targetBets",
																			header: "Target Bets",
																			cell: item => (
																				<Input
																					type="number"
																					value={item.targetBets}
																					onChange={({ detail }) =>
																						this.updateBotConfigBotField(item.slot - 1, "targetBets", detail.value)
																					}
																					placeholder="500"
																				/>
																			),
																			width: 160,
																		},
																...(this.isSumOfWinningsTournament()
																	? [
																			{
																				id: "winAmount",
																				header: "Win Amount (RUB)",
																				cell: item => (
																					<SpaceBetween direction="horizontal" size="xs">
																						<Input
																							type="number"
																							value={item.winAmount.min}
																							step="any"
																							onChange={({ detail }) =>
																								this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "winAmount")
																							}
																							placeholder="Min"
																						/>
																						<Input
																							type="number"
																							value={item.winAmount.max}
																							step="any"
																							onChange={({ detail }) =>
																								this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "winAmount")
																							}
																							placeholder="Max"
																						/>
																					</SpaceBetween>
																				),
																				width: 260,
																			},
																		]
																	: []),
																{
																	id: "betInterval",
																	header: "Bet Interval (sec)",
																	cell: item => (
																		<SpaceBetween direction="horizontal" size="xs">
																			<Input
																				type="number"
																				value={item.betInterval.min}
																				step="any"
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "betInterval")
																				}
																				placeholder="Min"
																			/>
																			<Input
																				type="number"
																				value={item.betInterval.max}
																				step="any"
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "betInterval")
																				}
																				placeholder="Max"
																			/>
																		</SpaceBetween>
																	),
																	width: 260,
																},
																{
																	id: "pauseDuration",
																	header: "Pause Duration (min)",
																	cell: item => (
																		<SpaceBetween direction="horizontal" size="xs">
																			<Input
																				type="number"
																				value={item.pauseDuration.min}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "pauseDuration")
																				}
																				placeholder="Min"
																			/>
																			<Input
																				type="number"
																				value={item.pauseDuration.max}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "pauseDuration")
																				}
																				placeholder="Max"
																			/>
																		</SpaceBetween>
																	),
																	width: 280,
																},
																{
																	id: "pausesPerDay",
																	header: "Pauses Per Day",
																	cell: item => (
																		<SpaceBetween direction="horizontal" size="xs">
																			<Input
																				type="number"
																				value={item.pausesPerDay.min}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "pausesPerDay")
																				}
																				placeholder="Min"
																			/>
																			<Input
																				type="number"
																				value={item.pausesPerDay.max}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "pausesPerDay")
																				}
																				placeholder="Max"
																			/>
																		</SpaceBetween>
																	),
																	width: 240,
																},
																{
																	id: "startDelayMinutes",
																	header: "Start Delay (min)",
																	cell: item => (
																		<SpaceBetween direction="horizontal" size="xs">
																			<Input
																				type="number"
																				value={item.startDelayMinutes.min}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "startDelayMinutes")
																				}
																				placeholder="Min"
																			/>
																			<Input
																				type="number"
																				value={item.startDelayMinutes.max}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "startDelayMinutes")
																				}
																				placeholder="Max"
																			/>
																		</SpaceBetween>
																	),
																	width: 240,
																},
																{
																	id: "actions",
																	header: "Actions",
																	cell: item => (
																		<Button
																			variant="inline-link"
																			onClick={() => this.removeBotConfigBot(item.slot - 1)}
																		>
																			Remove bot
																		</Button>
																	),
																	width: 160,
																},
															]}
															items={botConfig.bots}
															empty={
																<Box textAlign="center" color="inherit">
																	No bots added
																</Box>
															}
															footer={
																<Button
																	variant="link"
																	onClick={this.addBotConfigBot}
																	disabled={botConfig.bots.length >= MAX_TOURNAMENT_BOTS}
																>
																	Add bot
																</Button>
															}
														/>
													</SpaceBetween>
												),
											},
											{
												label: "JSON",
												id: "json",
												content: (
													<SpaceBetween direction="vertical" size="m">
														<Box color="text-body-secondary">
															Paste the full bot config JSON. Valid changes are synced immediately and saved with the main Save button.
														</Box>
														<SpaceBetween direction="horizontal" size="xs">
															<Button onClick={this.copyBotConfigSelection}>
																Copy JSON
															</Button>
														</SpaceBetween>
														<FormField
															label="Bot config JSON"
															description={
																this.isSumOfWinningsTournament()
																	? "Fields: enabled, targetWinnings, winAmount, betInterval, pauseDuration, pausesPerDay, startDelayMinutes. Target winnings and win amount are in rubles and are multiplied by 100 before saving."
																	: "Fields: enabled, targetBets, betInterval, pauseDuration, pausesPerDay, startDelayMinutes"
															}
															errorText={this.state.botConfigError}
															stretch
														>
															<Textarea
																value={this.state.botConfigDraft}
																onChange={({ detail }) =>
																	this.handleBotConfigDraftChange(detail.value)
																}
																placeholder={
																	this.isSumOfWinningsTournament()
																		? `{\n  "enabled": true,\n  "bots": [\n    {\n      "slot": 1,\n      "targetWinnings": 50000,\n      "winAmount": { "min": 10, "max": 2000 },\n      "betInterval": { "min": 60, "max": 180 },\n      "pauseDuration": { "min": 5, "max": 15 },\n      "pausesPerDay": { "min": 1, "max": 2 },\n      "startDelayMinutes": { "min": 0, "max": 30 }\n    }\n  ]\n}`
																		: `{\n  "enabled": true,\n  "bots": [\n    {\n      "slot": 1,\n      "targetBets": 500,\n      "betInterval": { "min": 60, "max": 180 },\n      "pauseDuration": { "min": 5, "max": 15 },\n      "pausesPerDay": { "min": 1, "max": 2 },\n      "startDelayMinutes": { "min": 0, "max": 30 }\n    }\n  ]\n}`
																}
																rows={18}
															/>
														</FormField>
													</SpaceBetween>
												),
											},
										]}
									/>
								</SpaceBetween>
							</Container>
						)}

						{/* Даты проведения */}
						{tournamentsAddModal.modalType === "tournaments" && (
							<Container header={<Box variant="h2">Tournament Dates</Box>}>
								<FormField
									label="From/To"
									description="Select the start and end dates for the tournament"
									stretch
								>
									<DateRangePicker
										onChange={({ detail }) => {
											setTournamentsAddModal({
												...tournamentsAddModal,
												from: detail.value.startDate.split("+")[0],
												to: detail.value.endDate.split("+")[0],
												type: detail.value.type,
											});
										}}
										value={{
											startDate: tournamentsAddModal.from,
											endDate: tournamentsAddModal.to,
											type: "absolute",
										}}
										relativeOptions={[]}
										isValidRange={range => {
											if (range.type === "absolute") {
												const [startDateWithoutTime] = range.startDate.split("T");
												const [endDateWithoutTime] = range.endDate.split("T");
												if (!startDateWithoutTime || !endDateWithoutTime) {
													return {
														valid: false,
														errorMessage: "The selected date range is incomplete.",
													};
												}
												if (new Date(range.startDate) - new Date(range.endDate) > 0) {
													return {
														valid: false,
														errorMessage: "Start date must be before end date.",
													};
												}
											}
											return { valid: true };
										}}
										i18nStrings={{
											todayAriaLabel: "Today",
											nextMonthAriaLabel: "Next month",
											previousMonthAriaLabel: "Previous month",
											customRelativeRangeDurationLabel: "Duration",
											customRelativeRangeDurationPlaceholder: "Enter duration",
											customRelativeRangeOptionLabel: "Custom range",
											customRelativeRangeOptionDescription: "Define a custom range",
											customRelativeRangeUnitLabel: "Unit of time",
											formatRelativeRange: ({ startDate, endDate }) => {
												const start = new Date(startDate).toLocaleString("en-GB");
												const end = new Date(endDate).toLocaleString("en-GB");
												return `${start} - ${end}`;
											},
											formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
											dateTimeConstraintText: "Date and time must be within range.",
											renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
												`Selected from ${startDate} to ${endDate}`,
											labelRelativeRange: "Relative range",
											labelAbsoluteRange: "Absolute range",
											dateTimePickerAriaLabel: "Date and time picker",
											dateTimePickerLabel: "Date and Time",
											clearButtonAriaLabel: "Clear date range",
											relativeModeTitle: "Relative mode",
											absoluteModeTitle: "Absolute mode",
											cancelButtonLabel: "Cancel",
											applyButtonLabel: "Apply",
										}}
									/>
								</FormField>
							</Container>
						)}
					</SpaceBetween>
				}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="m">
							<Button
								variant="normal"
								onClick={() =>
									setTournamentsAddModal({ ...tournamentsAddModal, show: false })
								}
							>
								Close
							</Button>
							<Button
								variant="primary"
								disabled={this.state.loading || this.state.jsonError || this.state.botConfigError}
								onClick={this.addTournament}
							>
								Save
							</Button>
						</SpaceBetween>
					</Box>
				}
			/>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const tournamentsAddModal = useRecoilValue(tournamentsAddModalStore);
		const setTournamentsAddModal = useSetRecoilState(tournamentsAddModalStore);
		const tournaments = useRecoilValue(tournamentsStore);
		const setTournaments = useSetRecoilState(tournamentsStore);
		const game = useRecoilValue(gameStore);

		return (
			<Component
				{...props}
				tournaments={tournaments}
				setTournaments={setTournaments}
				setTournamentsAddModal={setTournamentsAddModal}
				tournamentsAddModal={tournamentsAddModal}
				game={game}
			/>
		);
	};
}

export default withRecoil(TournamentsAddModal);
